import { useEffect, useRef, useState } from 'react';
import { apiUrl, authHeaders } from '@/lib/shell';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { workspaceErrorCopy } from './workspace-errors';

export interface HistoryMatch {
  threadId: string;
  title: string;
  messageId?: string;
  role?: 'user' | 'assistant';
  excerpt: string;
  updatedAt: string;
  /** Collaboration v1: where the chat lives and whose it is. */
  visibility?: 'private' | 'members' | 'company';
  own?: boolean;
  ownerName?: string | null;
  /** An answer is being produced in this chat right now (the live dot). */
  active?: boolean;
  /** Projects v1: the project the chat lives in, when the reader can read it. */
  project?: { id: string; name: string; tint: string } | null;
}
/** Which chats a search covers (collaboration v1): `mine` the person's own
 * (the default), `recent` the Recent chats list, `all` every chat they can
 * read. */
export type HistoryScope = 'mine' | 'recent' | 'all';
export interface HistorySearchPage {
  items: HistoryMatch[];
  hasMore: boolean;
}
const PAGE_SIZE = 20;
export const MAX_HISTORY_MATCHES = 200;
export async function searchHistory(
  query: string,
  offset: number,
  signal: AbortSignal,
  scope?: HistoryScope,
  locale?: string,
): Promise<HistorySearchPage> {
  const copy = workspaceErrorCopy(locale);
  const params = new URLSearchParams({
    q: query.trim(),
    limit: String(PAGE_SIZE),
    offset: String(offset),
    ...(scope ? { scope } : {}),
  });
  const response = await fetch(apiUrl(`/api/v1/history/search?${params}`), {
    credentials: 'same-origin',
    headers: authHeaders(),
    signal,
  });
  if (!response.ok)
    throw new Error(
      response.status === 401
        ? copy.history401
        : response.status === 403
          ? copy.history403
          : copy.historyUnavailable,
    );
  const unreadable = copy.historyUnreadable;
  const value = await response.json().catch(() => {
    throw new Error(unreadable);
  });
  if (
    !value ||
    !Array.isArray(value.items) ||
    value.items.length > PAGE_SIZE ||
    typeof value.hasMore !== 'boolean' ||
    value.items.some(
      (item: HistoryMatch) =>
        !item ||
        typeof item.threadId !== 'string' ||
        typeof item.title !== 'string' ||
        typeof item.excerpt !== 'string' ||
        typeof item.updatedAt !== 'string' ||
        !Number.isFinite(Date.parse(item.updatedAt)) ||
        (item.role !== undefined && item.role !== 'user' && item.role !== 'assistant'),
    )
  )
    throw new Error(unreadable);
  return value;
}

type SearchState = HistorySearchPage & {
  key: string;
  offset: number;
  loading: boolean;
  error: string;
};
const initial: SearchState = {
  key: '',
  offset: 0,
  items: [],
  hasMore: false,
  loading: false,
  error: '',
};
/** One request per query/page; no previous-query or previous-user results can reappear. */
export function useHistorySearch(
  open: boolean,
  query: string,
  userId?: string,
  scope?: HistoryScope,
) {
  const locale = useChatInterfaceLocale();
  const key = `${userId ?? ''}\0${scope ?? ''}\0${query.trim()}`;
  const [state, setState] = useState(initial);
  const generation = useRef(0);
  const request = useRef<AbortController | null>(null);
  const mountedKey = useRef(key);
  mountedKey.current = key;
  async function load(offset: number, previous: HistoryMatch[], owner: number) {
    if (request.current) return;
    const controller = new AbortController();
    request.current = controller;
    setState({ key, offset, items: previous, hasMore: offset > 0, loading: true, error: '' });
    const timeout = setTimeout(() => controller.abort('timeout'), 12000);
    try {
      const page = await searchHistory(query, offset, controller.signal, scope, locale);
      if (owner !== generation.current || mountedKey.current !== key || controller.signal.aborted)
        return;
      setState({
        key,
        offset: offset + page.items.length,
        // A conversation can move between offset pages when another turn lands.
        items: [
          ...new Map([...previous, ...page.items].map((item) => [item.threadId, item])).values(),
        ],
        hasMore: page.hasMore && page.items.length > 0,
        loading: false,
        error: '',
      });
    } catch (error) {
      if (owner !== generation.current || mountedKey.current !== key) return;
      setState({
        key,
        offset,
        items: previous,
        hasMore: offset > 0,
        loading: false,
        error:
          controller.signal.reason === 'timeout'
            ? 'Conversation search timed out. Try again.'
            : error instanceof Error
              ? error.message
              : 'Conversation search is unavailable. Try again.',
      });
    } finally {
      clearTimeout(timeout);
      if (request.current === controller) request.current = null;
    }
  }
  useEffect(() => {
    const owner = ++generation.current;
    request.current?.abort();
    request.current = null;
    if (!open || !userId) {
      setState(initial);
      return;
    }
    setState({ key, offset: 0, items: [], hasMore: false, loading: true, error: '' });
    const timer = setTimeout(() => void load(0, [], owner), query.trim() ? 180 : 0);
    return () => {
      clearTimeout(timer);
      generation.current++;
      request.current?.abort();
      request.current = null;
    };
  }, [open, key]);
  const current =
    open && userId && state.key === key ? state : { ...initial, loading: Boolean(open && userId) };
  return {
    ...current,
    limited: current.offset >= MAX_HISTORY_MATCHES && current.hasMore,
    loadMore: () => {
      if (
        !open ||
        !userId ||
        !current.hasMore ||
        current.loading ||
        current.offset >= MAX_HISTORY_MATCHES
      )
        return;
      void load(current.offset, current.items, generation.current);
    },
    retry: () => {
      if (open && userId && !current.loading)
        void load(current.offset, current.items, generation.current);
    },
  };
}
